<template><div><code-example only-show="demo">
<dt-segmented-control v-model="selected" aria-label="View filter">
  <dt-segmented-control-item value="all">All</dt-segmented-control-item>
  <dt-segmented-control-item value="favorites">Favorites</dt-segmented-control-item>
  <dt-segmented-control-item value="recent">Recent</dt-segmented-control-item>
  <dt-segmented-control-item value="groups">Groups</dt-segmented-control-item>
</dt-segmented-control>
</code-example>
<component-combinator component-name="DtSegmentedControl" /><h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">A segmented control is a <strong>mutually exclusive</strong>, single-select control where all options are visible at once. Only one item can be selected at a time, and there is always one item selected. It is commonly used to switch between different <strong>views or formats of the same content</strong> — such as toggling between grid and list view, or filtering between &quot;All&quot;, &quot;Favorites&quot;, and &quot;Recent&quot;.</p>
<p class="d-docsite--paragraph">Each <code v-pre>dt-segmented-control-item</code> wraps a <code v-pre>DtButton</code> internally and inherits its slot and prop surface, including <code v-pre>#startIcon</code>, <code v-pre>#endIcon</code>, <code v-pre>#leading</code>, <code v-pre>#trailing</code>, <code v-pre>disabled</code>, and <code v-pre>label-class</code>.</p>
<h3 id="segmented-control-vs-tabs" tabindex="-1" class="d-docsite--header-3">Segmented Control vs. Tabs <a class="header-anchor d-link d-docsite--link d-link" href="#segmented-control-vs-tabs" aria-hidden="true">#</a></h3>
<table>
<thead>
<tr>
<th></th>
<th>Segmented Control</th>
<th>Tabs</th>
</tr>
</thead>
<tbody>
<tr>
<td><strong>Purpose</strong></td>
<td>Switch between <strong>views or formats</strong> of the same content</td>
<td>Navigate between <strong>distinct content sections</strong></td>
</tr>
<tr>
<td><strong>Content relationship</strong></td>
<td>Same data, different presentation (e.g., grid vs. list)</td>
<td>Different data in each section (the filing cabinet metaphor — each tab holds different papers)</td>
</tr>
<tr>
<td><strong>Hierarchy</strong></td>
<td>Lower-level, often <strong>within</strong> a tabbed section</td>
<td>Top-level content organization</td>
</tr>
<tr>
<td><strong>ARIA role</strong></td>
<td><code v-pre>radiogroup</code> with <code v-pre>radio</code> items</td>
<td><code v-pre>tablist</code> with <code v-pre>tab</code> items and associated <code v-pre>tabpanel</code>s</td>
</tr>
<tr>
<td><strong>Content panel</strong></td>
<td>None — consumers manage their own view switching</td>
<td>Built-in panel association via <code v-pre>aria-controls</code></td>
</tr>
</tbody>
</table>
<h3 id="guidelines" tabindex="-1" class="d-docsite--header-3">Guidelines <a class="header-anchor d-link d-docsite--link d-link" href="#guidelines" aria-hidden="true">#</a></h3>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use when presenting <strong>2–5 mutually exclusive options</strong> that control the same content area.</li>
<li class="d-docsite--list-element">Use concise labels — <strong>1–3 words</strong> that describe the view the user will see.</li>
<li class="d-docsite--list-element">Keep label lengths consistent across items for visual balance.</li>
<li class="d-docsite--list-element">Use for switching between <strong>views or formats</strong>: grid/list, all/filtered, map/satellite.</li>
<li class="d-docsite--list-element">Provide an <code v-pre>aria-label</code> on the segmented control that describes the purpose of the group.</li>
<li class="d-docsite--list-element">Use the <code v-pre>label</code> prop on icon-only items for accessibility.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Don't use for <strong>navigation between distinct content sections</strong> — use <dt-link to="tabs.html">Tabs</dt-link> instead.</li>
<li class="d-docsite--list-element">Don't use for <strong>binary on/off choices</strong> — use a <dt-link to="toggle.html">Toggle</dt-link> instead.</li>
<li class="d-docsite--list-element">Don't use for <strong>more than 5 options</strong> — consider a <dt-link to="select-menu.html">Select Menu</dt-link> or <dt-link to="chip.html">Chip</dt-link> group.</li>
<li class="d-docsite--list-element">Don't mix text-only and icon-only items within the same control.</li>
<li class="d-docsite--list-element">Don't use to <strong>trigger actions</strong> — use a <dt-link to="button.html">Button</dt-link> or <dt-link to="button-group.html">Button Group</dt-link> instead.</li>
</ul>
</template>
</dialtone-usage>
<h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-segmented-control v-model=&quot;selected&quot; aria-label=&quot;View filter&quot;&gt;
  &lt;dt-segmented-control-item value=&quot;all&quot;&gt;All&lt;/dt-segmented-control-item&gt;
  &lt;dt-segmented-control-item value=&quot;favorites&quot;&gt;Favorites&lt;/dt-segmented-control-item&gt;
  &lt;dt-segmented-control-item value=&quot;recent&quot;&gt;Recent&lt;/dt-segmented-control-item&gt;
  &lt;dt-segmented-control-item value=&quot;groups&quot;&gt;Groups&lt;/dt-segmented-control-item&gt;
&lt;/dt-segmented-control&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-segmented-control v-model="selected" aria-label="View filter">
    <dt-segmented-control-item value="all">All</dt-segmented-control-item>
    <dt-segmented-control-item value="favorites">Favorites</dt-segmented-control-item>
    <dt-segmented-control-item value="recent">Recent</dt-segmented-control-item>
    <dt-segmented-control-item value="groups">Groups</dt-segmented-control-item>
  </dt-segmented-control>
</div>
</code-example>
<h3 id="borderless" tabindex="-1" class="d-docsite--header-3">Borderless <a class="header-anchor d-link d-docsite--link d-link" href="#borderless" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Remove the border and padding from the container.</p>
<code-example source-code='&lt;dt-segmented-control v-model=&quot;selected&quot; borderless&gt;
  ...
&lt;/dt-segmented-control&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-segmented-control v-model="selected" borderless aria-label="View filter">
    <dt-segmented-control-item value="all">All</dt-segmented-control-item>
    <dt-segmented-control-item value="favorites">Favorites</dt-segmented-control-item>
    <dt-segmented-control-item value="recent">Recent</dt-segmented-control-item>
    <dt-segmented-control-item value="groups">Groups</dt-segmented-control-item>
  </dt-segmented-control>
</div>
</code-example>
<h3 id="hide-divider" tabindex="-1" class="d-docsite--header-3">Hide Divider <a class="header-anchor d-link d-docsite--link d-link" href="#hide-divider" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-segmented-control v-model=&quot;selected&quot; :show-divider=&quot;false&quot;&gt;
  ...
&lt;/dt-segmented-control&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-segmented-control v-model="selected" :show-divider="false" aria-label="View filter">
    <dt-segmented-control-item value="all">All</dt-segmented-control-item>
    <dt-segmented-control-item value="favorites">Favorites</dt-segmented-control-item>
    <dt-segmented-control-item value="recent">Recent</dt-segmented-control-item>
    <dt-segmented-control-item value="groups">Groups</dt-segmented-control-item>
  </dt-segmented-control>
</div>
</code-example>
<h3 id="spread-evenly" tabindex="-1" class="d-docsite--header-3">Spread Evenly <a class="header-anchor d-link d-docsite--link d-link" href="#spread-evenly" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Items share space equally. Only applies in horizontal orientation.</p>
<code-example source-code='&lt;dt-segmented-control v-model=&quot;selected&quot; spread=&quot;evenly&quot;&gt;
  ...
&lt;/dt-segmented-control&gt;'>
<div data-demo-wrapper class="d-w-750">
  <dt-segmented-control v-model="spreadSelected" spread="evenly" aria-label="Spread example">
    <dt-segmented-control-item value="1">1</dt-segmented-control-item>
    <dt-segmented-control-item value="two">Two</dt-segmented-control-item>
    <dt-segmented-control-item value="three">Three</dt-segmented-control-item>
    <dt-segmented-control-item value="four">Four long label</dt-segmented-control-item>
  </dt-segmented-control>
</div>
</code-example>
<h3 id="disabled" tabindex="-1" class="d-docsite--header-3">Disabled <a class="header-anchor d-link d-docsite--link d-link" href="#disabled" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Add <code v-pre>disabled</code> to the group to disable all items.</p>
<code-example source-code='&lt;dt-segmented-control v-model=&quot;selected&quot; disabled&gt;
  ...
&lt;/dt-segmented-control&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-segmented-control v-model="selected" disabled aria-label="Disabled example" :show-divider="false">
    <dt-segmented-control-item value="all">All</dt-segmented-control-item>
    <dt-segmented-control-item value="favorites">Favorites</dt-segmented-control-item>
    <dt-segmented-control-item value="recent">Recent</dt-segmented-control-item>
  </dt-segmented-control>
</div>
</code-example>
<p class="d-docsite--paragraph">Add <code v-pre>disabled</code> to an individual item.</p>
<code-example source-code='&lt;dt-segmented-control v-model=&quot;selected&quot; aria-label=&quot;Individual disabled example&quot;&gt;
  &lt;dt-segmented-control-item value=&quot;all&quot;&gt;All&lt;/dt-segmented-control-item&gt;
  &lt;dt-segmented-control-item value=&quot;favorites&quot; disabled&gt;Favorites&lt;/dt-segmented-control-item&gt;
  &lt;dt-segmented-control-item value=&quot;recent&quot;&gt;Recent&lt;/dt-segmented-control-item&gt;
  &lt;dt-segmented-control-item value=&quot;groups&quot;&gt;Groups&lt;/dt-segmented-control-item&gt;
&lt;/dt-segmented-control&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-segmented-control v-model="selected" aria-label="Individual disabled example">
    <dt-segmented-control-item value="all">All</dt-segmented-control-item>
    <dt-segmented-control-item value="favorites" disabled>Favorites</dt-segmented-control-item>
    <dt-segmented-control-item value="recent">Recent</dt-segmented-control-item>
    <dt-segmented-control-item value="groups">Groups</dt-segmented-control-item>
  </dt-segmented-control>
</div>
</code-example>
<h2 id="sizes" tabindex="-1" class="d-docsite--header-2">Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#sizes" aria-hidden="true">#</a></h2>
<code-example source-code='&lt;dt-segmented-control v-model=&quot;selected&quot; :size=&quot;100|200|300|400|500&quot;&gt;
  ...
&lt;/dt-segmented-control&gt;'>
<dt-stack data-demo-wrapper gap="100" class="d-w100p">
  <dt-segmented-control v-model="selected" :size="100" aria-label="Extra small">
    <dt-segmented-control-item value="all">All</dt-segmented-control-item>
    <dt-segmented-control-item value="favorites">Favorites</dt-segmented-control-item>
    <dt-segmented-control-item value="recent">Recent</dt-segmented-control-item>
  </dt-segmented-control>
  <dt-segmented-control v-model="selected" aria-label="Small (default)">
    <dt-segmented-control-item value="all">All</dt-segmented-control-item>
    <dt-segmented-control-item value="favorites">Favorites</dt-segmented-control-item>
    <dt-segmented-control-item value="recent">Recent</dt-segmented-control-item>
  </dt-segmented-control>
  <dt-segmented-control v-model="selected" :size="300" aria-label="Medium">
    <dt-segmented-control-item value="all">All</dt-segmented-control-item>
    <dt-segmented-control-item value="favorites">Favorites</dt-segmented-control-item>
    <dt-segmented-control-item value="recent">Recent</dt-segmented-control-item>
  </dt-segmented-control>
  <dt-segmented-control v-model="selected" :size="400" aria-label="Large">
    <dt-segmented-control-item value="all">All</dt-segmented-control-item>
    <dt-segmented-control-item value="favorites">Favorites</dt-segmented-control-item>
    <dt-segmented-control-item value="recent">Recent</dt-segmented-control-item>
  </dt-segmented-control>
  <dt-segmented-control v-model="selected" :size="500" aria-label="Extra large">
    <dt-segmented-control-item value="all">All</dt-segmented-control-item>
    <dt-segmented-control-item value="favorites">Favorites</dt-segmented-control-item>
    <dt-segmented-control-item value="recent">Recent</dt-segmented-control-item>
  </dt-segmented-control>
</dt-stack>
</code-example>
<h2 id="slots" tabindex="-1" class="d-docsite--header-2">Slots <a class="header-anchor d-link d-docsite--link d-link" href="#slots" aria-hidden="true">#</a></h2>
<h3 id="icon" tabindex="-1" class="d-docsite--header-3">Icon <a class="header-anchor d-link d-docsite--link d-link" href="#icon" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use the <code v-pre>#startIcon</code> or <code v-pre>#endIcon</code> slot on <code v-pre>dt-segmented-control-item</code> to add an icon. The slot provides <code v-pre>iconSize</code> to match the control's size.</p>
<code-example source-code='&lt;dt-segmented-control v-model=&quot;iconSelected&quot; aria-label=&quot;List spacing&quot;&gt;
  &lt;dt-segmented-control-item value=&quot;compact&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon name=&quot;list-spacing-compact&quot; :size=&quot;iconSize&quot; /&gt;
    &lt;/template&gt;
    Compact
  &lt;/dt-segmented-control-item&gt;
  &lt;dt-segmented-control-item value=&quot;regular&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon name=&quot;list-spacing-regular&quot; :size=&quot;iconSize&quot; /&gt;
    &lt;/template&gt;
    Regular
  &lt;/dt-segmented-control-item&gt;
  &lt;dt-segmented-control-item value=&quot;expanded&quot;&gt;
    &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
      &lt;dt-icon name=&quot;list-spacing-expanded&quot; :size=&quot;iconSize&quot; /&gt;
    &lt;/template&gt;
    Expanded
  &lt;/dt-segmented-control-item&gt;
&lt;/dt-segmented-control&gt;'>
<div data-demo-wrapper class="d-w100p">
  <dt-segmented-control v-model="iconSelected" aria-label="List spacing">
    <dt-segmented-control-item value="compact">
      <template #startIcon="{ iconSize }">
        <dt-icon name="list-spacing-compact" :size="iconSize" />
      </template>
      Compact
    </dt-segmented-control-item>
    <dt-segmented-control-item value="regular">
      <template #startIcon="{ iconSize }">
        <dt-icon name="list-spacing-regular" :size="iconSize" />
      </template>
      Regular
    </dt-segmented-control-item>
    <dt-segmented-control-item value="expanded">
      <template #startIcon="{ iconSize }">
        <dt-icon name="list-spacing-expanded" :size="iconSize" />
      </template>
      Expanded
    </dt-segmented-control-item>
  </dt-segmented-control>
</div>
</code-example>
<h3 id="icon-only" tabindex="-1" class="d-docsite--header-3">Icon Only <a class="header-anchor d-link d-docsite--link d-link" href="#icon-only" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Omit the default slot text to create icon-only items. Use the <code v-pre>label</code> prop for accessibility.</p>
<code-example source-code='&lt;div class=&quot;d-w-750&quot;&gt;
  &lt;dt-segmented-control v-model=&quot;iconOnlySelected&quot; aria-label=&quot;Appearance mode&quot;&gt;
    &lt;dt-segmented-control-item value=&quot;system&quot; label=&quot;System&quot;&gt;
      &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
        &lt;dt-icon name=&quot;laptop-2&quot; :size=&quot;iconSize&quot; /&gt;
      &lt;/template&gt;
    &lt;/dt-segmented-control-item&gt;
    &lt;dt-segmented-control-item value=&quot;light&quot; label=&quot;Light&quot;&gt;
      &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
        &lt;dt-icon name=&quot;sun&quot; :size=&quot;iconSize&quot; /&gt;
      &lt;/template&gt;
    &lt;/dt-segmented-control-item&gt;
    &lt;dt-segmented-control-item value=&quot;dark&quot; label=&quot;Dark&quot;&gt;
      &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
        &lt;dt-icon name=&quot;moon&quot; :size=&quot;iconSize&quot; /&gt;
      &lt;/template&gt;
    &lt;/dt-segmented-control-item&gt;
  &lt;/dt-segmented-control&gt;
&lt;/div&gt;
&lt;div&gt;
  &lt;dt-segmented-control v-model=&quot;iconOnlySelected&quot; aria-label=&quot;Appearance mode&quot; class=&quot;d-d-inline-flex&quot;&gt;
    &lt;dt-segmented-control-item value=&quot;system&quot; label=&quot;System&quot;&gt;
      &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
        &lt;dt-icon name=&quot;laptop-2&quot; :size=&quot;iconSize&quot; /&gt;
      &lt;/template&gt;
    &lt;/dt-segmented-control-item&gt;
    &lt;dt-segmented-control-item value=&quot;light&quot; label=&quot;Light&quot;&gt;
      &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
        &lt;dt-icon name=&quot;sun&quot; :size=&quot;iconSize&quot; /&gt;
      &lt;/template&gt;
    &lt;/dt-segmented-control-item&gt;
    &lt;dt-segmented-control-item value=&quot;dark&quot; label=&quot;Dark&quot;&gt;
      &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
        &lt;dt-icon name=&quot;moon&quot; :size=&quot;iconSize&quot; /&gt;
      &lt;/template&gt;
    &lt;/dt-segmented-control-item&gt;
  &lt;/dt-segmented-control&gt;
&lt;/div&gt;'>
<dt-stack data-demo-wrapper gap="100" align="center">
  <div class="d-w-750">
    <dt-segmented-control v-model="iconOnlySelected" aria-label="Appearance mode">
      <dt-segmented-control-item value="system" label="System">
        <template #startIcon="{ iconSize }">
          <dt-icon name="laptop-2" :size="iconSize" />
        </template>
      </dt-segmented-control-item>
      <dt-segmented-control-item value="light" label="Light">
        <template #startIcon="{ iconSize }">
          <dt-icon name="sun" :size="iconSize" />
        </template>
      </dt-segmented-control-item>
      <dt-segmented-control-item value="dark" label="Dark">
        <template #startIcon="{ iconSize }">
          <dt-icon name="moon" :size="iconSize" />
        </template>
      </dt-segmented-control-item>
    </dt-segmented-control>
  </div>
  <div>
    <dt-segmented-control v-model="iconOnlySelected" aria-label="Appearance mode" class="d-d-inline-flex">
      <dt-segmented-control-item value="system" label="System">
        <template #startIcon="{ iconSize }">
          <dt-icon name="laptop-2" :size="iconSize" />
        </template>
      </dt-segmented-control-item>
      <dt-segmented-control-item value="light" label="Light">
        <template #startIcon="{ iconSize }">
          <dt-icon name="sun" :size="iconSize" />
        </template>
      </dt-segmented-control-item>
      <dt-segmented-control-item value="dark" label="Dark">
        <template #startIcon="{ iconSize }">
          <dt-icon name="moon" :size="iconSize" />
        </template>
      </dt-segmented-control-item>
    </dt-segmented-control>
  </div>
</dt-stack>
</code-example>
<h3 id="leading-trailing" tabindex="-1" class="d-docsite--header-3">Leading &amp; Trailing <a class="header-anchor d-link d-docsite--link d-link" href="#leading-trailing" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use the <code v-pre>#leading</code> and <code v-pre>#trailing</code> slots on <code v-pre>dt-segmented-control-item</code> to render content alongside labels, such as badges or count indicators.</p>
<code-example source-code='&lt;dt-segmented-control v-model=&quot;trailingSelected&quot; aria-label=&quot;Fruit counts&quot;&gt;
  &lt;dt-segmented-control-item value=&quot;apples&quot; trailingClass=&quot;d-pie-100&quot;&gt;
    Apples
    &lt;template #trailing&gt;
      &lt;dt-badge kind=&quot;count&quot;&gt;24&lt;/dt-badge&gt;
    &lt;/template&gt;
  &lt;/dt-segmented-control-item&gt;
  &lt;dt-segmented-control-item value=&quot;oranges&quot; trailingClass=&quot;d-pie-100&quot;&gt;
    Oranges
    &lt;template #trailing&gt;
      &lt;dt-badge kind=&quot;count&quot;&gt;8&lt;/dt-badge&gt;
    &lt;/template&gt;
  &lt;/dt-segmented-control-item&gt;
  &lt;dt-segmented-control-item value=&quot;bananas&quot; trailingClass=&quot;d-pie-100&quot;&gt;
    Bananas
    &lt;template #trailing&gt;
      &lt;dt-badge kind=&quot;count&quot;&gt;15&lt;/dt-badge&gt;
    &lt;/template&gt;
  &lt;/dt-segmented-control-item&gt;
&lt;/dt-segmented-control&gt;'>
<dt-segmented-control v-model="trailingSelected" aria-label="Fruit counts">
  <dt-segmented-control-item value="apples" trailingClass="d-pie-100">
    Apples
    <template #trailing>
      <dt-badge kind="count">24</dt-badge>
    </template>
  </dt-segmented-control-item>
  <dt-segmented-control-item value="oranges" trailingClass="d-pie-100">
    Oranges
    <template #trailing>
      <dt-badge kind="count">8</dt-badge>
    </template>
  </dt-segmented-control-item>
  <dt-segmented-control-item value="bananas" trailingClass="d-pie-100">
    Bananas
    <template #trailing>
      <dt-badge kind="count">15</dt-badge>
    </template>
  </dt-segmented-control-item>
</dt-segmented-control>
</code-example>
<h2 id="orientation" tabindex="-1" class="d-docsite--header-2">Orientation <a class="header-anchor d-link d-docsite--link d-link" href="#orientation" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Set <code v-pre>orientation=&quot;vertical&quot;</code> to stack items vertically.</p>
<code-example source-code='&lt;dt-segmented-control v-model=&quot;selected&quot; orientation=&quot;vertical&quot;&gt;
  ...
&lt;/dt-segmented-control&gt;'>
<dt-stack direction="row" gap="200" align="start">
  <div class="d-w-250">
    <dt-segmented-control v-model="selected" orientation="vertical" aria-label="Vertical example">
      <dt-segmented-control-item value="all">
        System
        <template #startIcon="{ iconSize }">
          <dt-icon name="laptop-2" :size="iconSize" />
        </template>
      </dt-segmented-control-item>
      <dt-segmented-control-item value="favorites">
        Light
        <template #startIcon="{ iconSize }">
          <dt-icon name="sun" :size="iconSize" />
        </template>
      </dt-segmented-control-item>
      <dt-segmented-control-item value="recent">
        Dark
        <template #startIcon="{ iconSize }">
          <dt-icon name="moon" :size="iconSize" />
        </template>
      </dt-segmented-control-item>
    </dt-segmented-control>
  </div>
  <dt-segmented-control v-model="iconOnlySelected" aria-label="Appearance mode" orientation="vertical">
    <dt-segmented-control-item value="system" label="System">
      <template #startIcon="{ iconSize }">
        <dt-icon name="laptop-2" :size="iconSize" />
      </template>
    </dt-segmented-control-item>
    <dt-segmented-control-item value="light" label="Light">
      <template #startIcon="{ iconSize }">
        <dt-icon name="sun" :size="iconSize" />
      </template>
    </dt-segmented-control-item>
    <dt-segmented-control-item value="dark" label="Dark">
      <template #startIcon="{ iconSize }">
        <dt-icon name="moon" :size="iconSize" />
      </template>
    </dt-segmented-control-item>
  </dt-segmented-control>
</dt-stack>
</code-example>
<h2 id="advanced-usages" tabindex="-1" class="d-docsite--header-2">Advanced Usages <a class="header-anchor d-link d-docsite--link d-link" href="#advanced-usages" aria-hidden="true">#</a></h2>
<h3 id="manual-activation-mode" tabindex="-1" class="d-docsite--header-3">Manual Activation Mode <a class="header-anchor d-link d-docsite--link d-link" href="#manual-activation-mode" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">By default, items select immediately on focus via arrow keys, following the <a class="d-link" href="https://www.w3.org/WAI/ARIA/apg/patterns/radio/" target="_blank">WAI-ARIA Radio Group pattern</a>. Set <code v-pre>activation-mode=&quot;manual&quot;</code> to require an explicit <code v-pre>Enter</code> or <code v-pre>Space</code> keypress after focusing an item.</p>
<code-example source-code='&lt;dt-segmented-control v-model=&quot;selected&quot; activation-mode=&quot;manual&quot;&gt;
  ...
&lt;/dt-segmented-control&gt;'>
<dt-segmented-control v-model="selected" activation-mode="manual" aria-label="Manual activation">
  <dt-segmented-control-item value="all">All</dt-segmented-control-item>
  <dt-segmented-control-item value="favorites">Favorites</dt-segmented-control-item>
  <dt-segmented-control-item value="recent">Recent</dt-segmented-control-item>
</dt-segmented-control>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<h3 id="segmented-control" tabindex="-1" class="d-docsite--header-3">Segmented Control <a class="header-anchor d-link d-docsite--link d-link" href="#segmented-control" aria-hidden="true">#</a></h3>
<component-vue-api component-name="segmentedcontrol" :also-import="['segmentedcontrolitem']" /><h3 id="segmented-control-item" tabindex="-1" class="d-docsite--header-3">Segmented Control Item <a class="header-anchor d-link d-docsite--link d-link" href="#segmented-control-item" aria-hidden="true">#</a></h3>
<component-vue-api component-name="segmentedcontrolitem" :show-import="false" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="segmented-control" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The segmented control implements the <a class="d-link" href="https://www.w3.org/WAI/ARIA/apg/patterns/radio/" target="_blank">WAI-ARIA Radio Group pattern</a> with roving tabindex for keyboard navigation.</p>
<component-accessible-table component-name="segmented-control" /></div></template>


<script setup>
import { ref } from 'vue';

const selected = ref('all');
const spreadSelected = ref('1');
const iconSelected = ref('compact');
const iconOnlySelected = ref('system');
const trailingSelected = ref('apples');
</script>